/**
 * `MenuSheet` — secondary-navigation surface mounted by `Layout`.
 *
 * Render order (top to bottom), proto-aligned:
 *
 *   1. Header — visible X close in the top-right; the title (`Valikko`)
 *      is sr-only because the prototype's drawer carries no visible
 *      title and the X icon is the universal "close" affordance.
 *   2. Chip — link to /profile (signed-in only). Avatar + display name
 *      + demo member number + demo badge + sr-only destination hint.
 *   3. Service navigation — proto order (re-aligned by 026/US2):
 *        - Lakikaveri (Scale icon) → expandable disclosure with 3 child
 *          NavLinks (Tutustu Lakikaveriin, Oikeusturvavakuutus, Lakiruutu)
 *        - Jäsenyys ja edut (Gift icon) → expandable disclosure with 2
 *          child NavLinks (Verkkokoulutuspalvelu, Edut ja alennukset)
 *        - Tapaturmavakuutus (Shield icon) → /vapaa-ajan-tapaturmavakuutus
 *        - Ota yhteyttä (ChatBubble icon) → opens the compose wizard
 *          (027 US6 / D-120 — the app's single contact experience)
 *        - Two legal external links (Sivuston käyttöehdot,
 *          Tietosuojaseloste) → ytkpalvelut.fi, opened in a new tab
 *          (026/US2 — moved here out of the front-page footer)
 *      The two expandable parents follow the WAI Disclosure pattern
 *      (research R-004): button toggles a `role="group"` region, ARIA
 *      `aria-expanded` + `aria-controls` wire the parent to the region,
 *      region id is per-instance via `useId()` (research R-006). Tab
 *      order is the natural document order — Tab from parent button
 *      into the first child link when expanded; Tab past the last
 *      child to the next top-level row. No menubar / arrow-key
 *      navigation.
 *   4. Separator (hr) — divides the service-nav block from the chrome
 *      below.
 *   5. Language picker — a full-width row with a leading GlobeIcon and the
 *      house Select exposing the Suomi, English, and Svenska endonyms.
 *      Always rendered; legitimate on `/login` too.
 *   6. Sign-out — full-width destructive-tinted row with a leading
 *      sign-out icon (signed-in only, no border, proto pattern).
 *   7. CMS pill — bottom-pinned (`mt-auto`) primary-fill link to
 *      `/admin/cms` (the authoring shell). Rendered ONLY for
 *      admin-role sessions; members and anonymous visitors do not
 *      see the affordance at all — admin functionality is not
 *      surfaced to non-admin users. The admin surfaces (`/admin/cms`
 *      and the `/admin` launch portal) are `role: 'admin'`-gated: an
 *      authenticated member who reaches either by URL bounces to
 *      `/forbidden`, while an anonymous visitor full-page redirects to
 *      the server admin sign-in (`/admin/login?returnTo=…`), so the menu
 *      has no reason to advertise them to non-admins.
 *
 * Spec refs: FR-001 amended (D-048 — menu sheet hosts the secondary-
 * nav surface), FR-010 + FR-058 (anonymous menu MUST NOT advertise
 * member-only affordances), FR-015 (hit-target ≥44 CSS px), FR-018
 * (expandable submenus replace the deep-link shortcut shape), FR-033
 * (keyboard reachability), FR-040 (Finnish-first copy), FR-056 (the
 * menu sheet hosts the user chip, service-nav, language control,
 * sign-out, and deferred entries).
 *
 * ## Anonymous menu posture (D-130: login wall restored)
 *
 * D-130 walls a logged-out visitor to `/login` (RouteGuard) and `Layout` hides
 * the Valikko trigger there, so anon never opens this menu in the running app;
 * the anonymous render branch below exists only for component tests (and the
 * brief pre-redirect frame). It shows the close button, the service-nav block
 * GREYED (aria-disabled, like a never-member — including "Ota yhteyttä", which
 * needs a compose identity anon lacks), the language picker (unconditional —
 * locale choice is supported), and an in-app "Kirjaudu sisään" row (-> /login)
 * replacing sign-out; the user chip + CMS pill stay absent (no profile / admin-only).
 *
 * Every utility class below is layout (`flex`, `gap-*`, `px-*`,
 * `py-*`) or a token-backed colour utility. No raw hex, no `*-[…]`
 * brackets.
 */

import { type JSX, useId, useState } from 'react';
import { NavLink, useNavigate } from 'react-router';

import { useTranslate } from '../i18n/useTranslate.js';
import { Sheet } from '../primitives/Sheet.js';
import { useSession } from '../session/useSession.js';

import { ChevronDownIcon, ChevronRightIcon, CloseIcon } from './icons.js';
import {
  assertNever,
  MemberChipContent,
  MemberNumberSkeleton,
  MenuActionEntryRow,
  MenuExternalLinkRow,
  MenuLanguageToggle,
  MenuSignInRow,
  MenuSignOutRow,
  useShellSignOut,
} from './MenuSheetHelpers.js';
import {
  menuChipClasses,
  menuCloseButtonClasses,
  menuCmsClasses,
  menuDividerClasses,
  menuHeaderClasses,
  menuLinkChevronClasses,
  menuLinkClasses,
  menuLinkDisabledClasses,
  menuLinkIconClasses,
  menuLinkLabelClasses,
  menuServiceNavClasses,
  menuSubmenuGroupClasses,
  sheetContentClasses,
} from './menuClasses.js';
import { type ExpandableKey, SERVICE_ENTRIES } from './menuServiceEntries.js';
import { useNavItemGating } from './navEntries.js';

// CMS authoring shell destination, surfaced in the menu for admins
// only. `/admin/cms` is a `protected: true, role: 'admin'` route in
// the registry — RouteGuard bounces a member session to `/forbidden`
// (AdminForbidden) and full-page redirects an anonymous session to the
// server admin sign-in (`/admin/login?returnTo=…`) for any direct URL
// navigation, but the menu itself does not advertise the affordance to
// non-admin users (admin functionality is not promoted to roles that
// cannot use it).
const CMS_TO = '/admin/cms';

// ---------- Component ----------

export interface MenuSheetProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly returnFocusTo?: HTMLElement | null;
}

export function MenuSheet({
  open,
  onOpenChange,
  returnFocusTo = null,
}: MenuSheetProps): JSX.Element {
  const t = useTranslate();
  const navigate = useNavigate();
  const session = useSession();

  // 017/US1 — submenu disclosure state. The set holds the keys of the
  // currently-expanded `expandable` parents (an empty set = all
  // collapsed). The "fresh state on every drawer open" contract
  // (B-INV-001) is structural: `Layout` keys this component on an
  // open-session counter (bumped only on closed→open, never on close),
  // so each open mounts a fresh instance and this initialiser (an empty
  // set = all submenus collapsed) IS the reset. Keying on the counter
  // rather than the `open` boolean keeps the instance mounted through
  // the close so Radix react-presence can animate `data-state="closed"`
  // (R10 — keys to reset state; R11 — no Effect for prop-driven reset).
  const [openSubmenus, setOpenSubmenus] = useState<ReadonlySet<ExpandableKey>>(() => new Set());

  // Per-instance ids for the two submenu regions (research R-006).
  // `useId()` keeps the binding stable across re-renders and unique
  // across hypothetical concurrent mounts. The `Record<ExpandableKey,
  // string>` annotation is load-bearing: a new `expandable` entry
  // without a matching `useId()` allocation here fails compile (see
  // `ExpandableKey` JSDoc in `menuServiceEntries.ts`).
  const lakikaveriSubmenuId = useId();
  const palvelutEdutSubmenuId = useId();
  const submenuIdsByKey: Record<ExpandableKey, string> = {
    lakikaveri: lakikaveriSubmenuId,
    palvelutEdut: palvelutEdutSubmenuId,
  };

  const toggleSubmenu = (key: ExpandableKey): void => {
    setOpenSubmenus((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const closeMenu = (): void => {
    onOpenChange(false);
  };

  const authState = session.state;
  const isSignedIn = authState.kind === 'member' || authState.kind === 'admin';
  const isAdmin = authState.kind === 'admin';
  // Anon is walled to `/login` under D-130 (see file header §"Anonymous menu
  // posture"); this branch serves component tests + the brief pre-redirect frame.
  const isAnonymous = authState.kind === 'anonymous';
  // Membership-state gating (028-membership-state-gating, AC4 / FR-010), shared
  // with the bottom nav and the desktop sidebar: service destinations grey for a
  // non-valid member and for an anonymous visitor; "Ota yhteyttä" greys for an
  // anonymous visitor only (FLAG 2). Admins are exempt.
  const isGreyed = useNavItemGating();
  const signOut = useShellSignOut();

  async function handleSignOut(): Promise<void> {
    closeMenu();
    await signOut();
  }

  function handleSignIn(): void {
    closeMenu();
    navigate('/login');
  }

  return (
    <Sheet.Root open={open} onOpenChange={onOpenChange}>
      <Sheet.Content side="left" className={sheetContentClasses}>
        <div className={menuHeaderClasses}>
          {/* Title is sr-only — the proto drawer carries no visible
              header; the X icon is the universal close affordance and
              its `aria-label` exposes the Finnish copy to AT users. */}
          <Sheet.Title className="sr-only">{t('shell.menu.title')}</Sheet.Title>
          <Sheet.Close className={menuCloseButtonClasses} aria-label={t('shell.menu.close')}>
            <CloseIcon aria-hidden="true" className="h-5 w-5" />
          </Sheet.Close>
        </div>
        <Sheet.Description className="sr-only">{t('shell.menu.description')}</Sheet.Description>

        {isSignedIn && (
          <NavLink to="/profile" onClick={closeMenu} className={menuChipClasses}>
            <MemberChipContent avatar numberFallback={<MemberNumberSkeleton />} />
          </NavLink>
        )}

        {(isSignedIn || isAnonymous) && (
          <nav aria-label={t('shell.menu.navLabel')} className={menuServiceNavClasses}>
            {SERVICE_ENTRIES.map((entry) => {
              const greyed = isGreyed({ source: 'service', entry });
              if (entry.kind === 'flat-link') {
                return (
                  <NavLink
                    key={entry.key}
                    to={entry.to}
                    onClick={greyed ? (e) => e.preventDefault() : closeMenu}
                    aria-disabled={greyed || undefined}
                    className={greyed ? menuLinkDisabledClasses : menuLinkClasses}
                  >
                    <entry.Icon aria-hidden="true" className={menuLinkIconClasses} />
                    <span className={menuLinkLabelClasses}>{t(entry.labelKey)}</span>
                  </NavLink>
                );
              }
              if (entry.kind === 'action') {
                // Greyed for an anonymous visitor only (`useNavItemGating`): the
                // entry follows its DESTINATION's shipped posture, entry-ungated
                // with flow-level gating (042/D-202), so a valid MEMBER keeps it
                // active (lead decision at PR-1 review; D-130, FLAG 2).
                return (
                  <MenuActionEntryRow
                    key={entry.key}
                    entry={entry}
                    closeMenu={closeMenu}
                    returnFocusTo={returnFocusTo}
                    disabled={greyed}
                  />
                );
              }
              if (entry.kind === 'external-link') {
                return <MenuExternalLinkRow key={entry.key} entry={entry} />;
              }
              if (entry.kind === 'expandable') {
                const submenuId = submenuIdsByKey[entry.key];
                const isOpen = openSubmenus.has(entry.key);
                const parentLabel = t(entry.labelKey);
                return (
                  <div key={entry.key}>
                    <button
                      type="button"
                      // A disabled disclosure must not advertise an expand action
                      // it can no longer perform (M5): drop aria-expanded when
                      // greyed; keep it wired when active.
                      aria-expanded={greyed ? undefined : isOpen}
                      aria-controls={submenuId}
                      aria-disabled={greyed || undefined}
                      onClick={
                        greyed
                          ? undefined
                          : () => {
                              toggleSubmenu(entry.key);
                            }
                      }
                      className={greyed ? menuLinkDisabledClasses : menuLinkClasses}
                    >
                      <entry.Icon aria-hidden="true" className={menuLinkIconClasses} />
                      <span className={menuLinkLabelClasses}>{parentLabel}</span>
                      {isOpen ? (
                        <ChevronDownIcon aria-hidden="true" className={menuLinkChevronClasses} />
                      ) : (
                        <ChevronRightIcon aria-hidden="true" className={menuLinkChevronClasses} />
                      )}
                    </button>
                    {/* biome-ignore lint/a11y/useSemanticElements: WAI Disclosure pattern (research R-004) — the submenu region is an ARIA `role="group"` `<div>`, NOT a `<fieldset>`. A `<fieldset>` would carry form-control semantics, default browser styling (border + padding + margin), and a `<legend>` accessibility model none of which suit a side-drawer disclosure region. The role is intentional. */}
                    <div
                      role="group"
                      id={submenuId}
                      hidden={!isOpen}
                      aria-label={`${parentLabel} ${t('shell.menu.submenuSuffix')}`}
                      className={menuSubmenuGroupClasses}
                    >
                      {/* Every submenu child is a navigating NavLink:
                          `MenuShortcutChild` is the single `child-link`
                          shape post-026/US2 (the deferred Valmennukset
                          variant was retired, spec FR-006), so no
                          per-child kind dispatch is needed here. The
                          top-level `MenuShortcut` dispatch keeps its
                          `assertNever` exhaustiveness guard below;
                          re-widening `MenuShortcutChild` into a union
                          again would reintroduce a child-level dispatch. */}
                      {entry.children.map((child) => (
                        <NavLink
                          key={child.key}
                          to={child.to}
                          onClick={greyed ? (e) => e.preventDefault() : closeMenu}
                          aria-disabled={greyed || undefined}
                          className={greyed ? menuLinkDisabledClasses : menuLinkClasses}
                        >
                          <span className={menuLinkLabelClasses}>{t(child.labelKey)}</span>
                        </NavLink>
                      ))}
                    </div>
                  </div>
                );
              }
              // Exhaustiveness guard (TS10) — the dispatch above handles
              // every `MenuShortcut` kind (`flat-link`, `action`,
              // `external-link`, `expandable`), so `entry` narrows to
              // `never` here. Adding
              // a new kind to the union without a matching branch fails
              // the compile at this call. Runtime throw is a safety net
              // only.
              return assertNever(entry);
            })}
          </nav>
        )}

        {(isSignedIn || isAnonymous) && <hr className={menuDividerClasses} />}

        {/* Language picker — always rendered (legitimate for anon and
            on /login). Sign-out (member/admin) is replaced for an anonymous
            visitor by the in-app "Kirjaudu sisään" row; under D-130 the login
            wall is restored, so this anon branch is the component-test / brief
            pre-redirect surface, not a browsable destination. */}
        <MenuLanguageToggle />

        {isSignedIn && <MenuSignOutRow onSignOut={() => void handleSignOut()} />}

        {isAnonymous && <MenuSignInRow onSignIn={handleSignIn} />}

        {isAdmin && (
          <NavLink to={CMS_TO} onClick={closeMenu} className={menuCmsClasses}>
            {t('shell.menu.cms')}
          </NavLink>
        )}
      </Sheet.Content>
    </Sheet.Root>
  );
}
